import { z } from "zod";
import { ALLOCATION_HEALTH_FILTERS } from "@/features/leave/leave-allocation-health-filter";
import type {
  LeaveSetupStatus,
  LeaveSetupStepKey,
} from "@/features/leave/schemas/leave-setup-status.schema";

/// URL search params for `/settings/leave`.
///
/// `tab` is here rather than in `useState` (S3) for the reason the sibling
/// attendance screen already put it here: a link to the Policies tab should be
/// a link somebody can send, the Back button should step between tabs, and a
/// refresh should keep the admin's place. Before this, all six tabs shared one
/// URL and a reload always landed on Leave types.
///
/// `.catch(undefined)` so an UNKNOWN tab degrades to the default instead of
/// throwing a SearchParamError at the router. The set of tabs a URL may name
/// shrinks over time; the URLs people saved do not.
export const leaveSettingsRouteSearchSchema = z
  .object({
    tab: z.enum(["plans", "calendar", "allocations"]).optional().catch(undefined),
    /// The focused view inside the Plans and people area. Types are a reusable
    /// library, not the first section of the plan workflow.
    plansView: z.enum(["plans", "types"]).optional().catch(undefined),
    /// The focused view inside Leave year and rules. It is URL state so links,
    /// refresh, and Back all retain the admin's active task.
    calendarView: z
      .enum(["years", "year-end", "blocked-dates", "rollover"])
      .optional()
      .catch(undefined),
    /// The generated leave year opened in the year-end workspace.
    leavePeriodId: z.string().optional(),
    /// One plan's year-end review, opened from its overview card.
    yearEndRunId: z.string().optional(),
    /// Opaque server cursors for the two paged review tables.
    yearEndPendingCursor: z.string().optional(),
    yearEndPlanCursor: z.string().optional(),
    /// Which plan is open in its focused editor. It remains shareable without
    /// turning the plan list into an inline editor.
    policyId: z.string().optional(),
    /// A focused editor state for a plan not yet persisted.
    planEditor: z.enum(["new"]).optional().catch(undefined),
    /// The focused task inside one persisted leave plan. This makes the
    /// post-create handoff to assigning people linkable and refresh-safe.
    policyView: z.enum(["details", "people"]).optional().catch(undefined),
    /// Which reusable leave type is open in its focused editor.
    typeId: z.string().optional(),
    /// A focused editor state for a type not yet persisted.
    typeEditor: z.enum(["new"]).optional().catch(undefined),
    /// The Allocations tab's three filters, so a filtered view is a link too.
    allocationMembershipId: z.string().optional(),
    allocationTypeId: z.string().optional(),
    allocationPeriodId: z.string().optional(),
    /// The allocation workspace has two different jobs: checking people and
    /// auditing allocation records. They are separate views, not filters.
    allocationView: z.enum(["people", "register"]).optional().catch(undefined),
    /// The selected allocation health card. This keeps a drill-down shareable
    /// and allows browser history to return to the prior people slice.
    allocationHealth: z.enum(ALLOCATION_HEALTH_FILTERS).optional().catch(undefined),
    /// A focused person's allocation record, kept separate from a register
    /// filter so Back returns to the same people-health view.
    allocationPersonId: z.string().optional(),
    /// The allocation open for amendment or adjustment, if any.
    allocationId: z.string().optional(),
  })
  .strict();

export type LeaveSettingsRouteSearch = z.infer<typeof leaveSettingsRouteSearchSchema>;
export type LeaveSettingsTab = NonNullable<LeaveSettingsRouteSearch["tab"]>;

/// Which tab fixes which setup step.
///
/// Five of the six map to themselves. Assigning people is the exception: an
/// assignment belongs to a policy, so "who holds nothing" is answered inside
/// the Policies tab rather than in a tab of its own. The readiness model keeps
/// it as a separate STEP because it fails separately, and this map is where
/// that distinction meets the tab bar.
const TAB_FOR_STEP: Record<LeaveSetupStepKey, LeaveSettingsTab> = {
  types: "plans",
  periods: "calendar",
  policies: "plans",
  assignments: "plans",
  blocks: "calendar",
  rollover: "calendar",
};

export function tabForSetupStep(key: LeaveSetupStepKey): LeaveSettingsTab {
  return TAB_FOR_STEP[key];
}

/// Where an unqualified visit lands: the tab of the first step in the way, else the tab an admin
/// returns to once setup is done. `optional` steps are things worth doing, never things in the way.
/// The URL still wins when it names a tab; this only decides the default.
export function defaultLeaveSettingsTab(status: LeaveSetupStatus | null): LeaveSettingsTab {
  if (!status) return "plans";
  const blocker =
    status.steps.find((step) => step.state === "needs_setup") ??
    status.steps.find((step) => step.state === "review");
  return tabForSetupStep(blocker?.key ?? "assignments");
}

/// Which steps a tab is answerable for. The Policies tab carries two.
export function setupStepsForTab(tab: LeaveSettingsTab): ReadonlyArray<LeaveSetupStepKey> {
  const keys = Object.keys(TAB_FOR_STEP) as LeaveSetupStepKey[];
  return keys.filter((key) => TAB_FOR_STEP[key] === tab);
}

/// Which tab the URL means.
///
/// A `policyId` names one policy, and policies only exist on the Policies tab,
/// so a link carrying one lands there whatever `tab` says. Without this,
/// `?policyId=x&tab=blocks` (a hand-edited or stale URL) would open Block
/// lists with a dead param and the policy the link was about would be nowhere.
export function resolveLeaveSettingsTab(
  search: LeaveSettingsRouteSearch,
  fallback: LeaveSettingsTab = "plans",
): LeaveSettingsTab {
  if (search.policyId || search.planEditor || search.typeId || search.typeEditor) return "plans";
  if (search.allocationId || search.allocationPersonId) return "allocations";
  if (search.yearEndRunId) return "calendar";
  return search.tab ?? fallback;
}
